/**
 * Bulk Student Upload Modal
 *
 * 5-step wizard for uploading students from CSV:
 * 1. File Upload
 * 2. Field Mapping
 * 3. Validation Preview
 * 4. Processing
 * 5. Results Summary
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

import {
  Upload,
  MapPin,
  CheckCircle2,
  Loader2,
  BarChart3,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Step components (to be imported)
import FileUploadStep from './bulk-upload/FileUploadStep';
import FieldMappingStep from './bulk-upload/FieldMappingStep';
import ValidationPreviewStep from './bulk-upload/ValidationPreviewStep';
import ProcessingStep from './bulk-upload/ProcessingStep';
import ResultsSummaryStep from './bulk-upload/ResultsSummaryStep';

const STEPS = [
  { id: 1, key: 'upload', icon: Upload },
  { id: 2, key: 'mapping', icon: MapPin },
  { id: 3, key: 'preview', icon: CheckCircle2 },
  { id: 4, key: 'processing', icon: Loader2 },
  { id: 5, key: 'results', icon: BarChart3 },
];

export default function BulkUploadModal({ isOpen, onClose, onComplete }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    file: null,
    csvData: null,
    fieldMapping: null,
    validationResults: null,
    taskId: null,
    uploadResults: null,
  });

  // Reset wizard state when modal closes and reopens
  useEffect(() => {
    if (!isOpen) {
      // Reset after modal closes (small delay to allow animations)
      const timeout = setTimeout(() => {
        setCurrentStep(1);
        setWizardData({
          file: null,
          csvData: null,
          fieldMapping: null,
          validationResults: null,
          taskId: null,
          uploadResults: null,
        });
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepData = (stepKey, data) => {
    setWizardData(prev => ({
      ...prev,
      [stepKey]: data,
    }));
  };

  const handleClose = () => {
    // Confirm if in middle of process
    if (currentStep > 1 && currentStep < 5) {
      if (!window.confirm(t('bulkUpload.confirmClose'))) {
        return;
      }
    }

    // Reset state
    setCurrentStep(1);
    setWizardData({
      file: null,
      csvData: null,
      fieldMapping: null,
      validationResults: null,
      taskId: null,
      uploadResults: null,
    });

    onClose();
  };

  const handleComplete = () => {
    onComplete(wizardData.uploadResults);
    handleClose();
  };

  const getCurrentStepComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FileUploadStep
            onNext={handleNext}
            onDataChange={(data) => handleStepData('csvData', data)}
            onFileChange={(file) => handleStepData('file', file)}
            initialFile={wizardData.file}
            initialCsvData={wizardData.csvData}
          />
        );
      case 2:
        return (
          <FieldMappingStep
            csvData={wizardData.csvData}
            fieldMapping={wizardData.fieldMapping}
            onNext={handleNext}
            onBack={handleBack}
            onMappingChange={(mapping) => handleStepData('fieldMapping', mapping)}
          />
        );
      case 3:
        return (
          <ValidationPreviewStep
            csvData={wizardData.csvData}
            fieldMapping={wizardData.fieldMapping}
            onNext={handleNext}
            onBack={handleBack}
            onValidationResults={(results) => handleStepData('validationResults', results)}
          />
        );
      case 4:
        return (
          <ProcessingStep
            file={wizardData.file}
            fieldMapping={wizardData.fieldMapping}
            onComplete={(results) => {
              handleStepData('uploadResults', results);
              handleNext();
            }}
            onTaskId={(taskId) => handleStepData('taskId', taskId)}
          />
        );
      case 5:
        return (
          <ResultsSummaryStep
            results={wizardData.uploadResults}
            onClose={handleComplete}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = (stepId) => {
    const step = STEPS.find(s => s.id === stepId);
    return t(`bulkUpload.steps.${step.key}.title`);
  };

  const canGoBack = currentStep > 1 && currentStep < 4;

  // Check if Next button should be enabled
  const canProceed = () => {
    if (currentStep === 1) {
      // Step 1 (File Upload): Require file to be uploaded and parsed
      return wizardData.csvData !== null;
    }
    if (currentStep === 2) {
      // Step 2 (Field Mapping): Require all 10 required fields to be mapped
      const REQUIRED_FIELDS = [
        'first_name', 'last_name', 'birthday', 'gender', 'parent_email',
        'parent_phone', 'course', 'school_year', 'address_1', 'home_county'
      ];
      const mappedFields = Object.values(wizardData.fieldMapping || {});
      return REQUIRED_FIELDS.every(field => mappedFields.includes(field));
    }
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {t('bulkUpload.title')}
          </DialogTitle>

          {/* Step Indicator */}
          <div className="mt-6">
            <div className="flex items-center">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isActive = step.id === currentStep;
                const isCompleted = step.id < currentStep;

                return (
                  <React.Fragment key={step.id}>
                    {/* Step Container */}
                    <div className="flex flex-col items-center flex-1">
                      {/* Step Circle */}
                      <div
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center transition-colors
                          ${isActive ? 'bg-blue-600 text-white dark:bg-blue-500' : ''}
                          ${isCompleted ? 'bg-green-600 text-white dark:bg-green-500' : ''}
                          ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500' : ''}
                        `}
                      >
                        <Icon className="w-5 h-5" />
                      </div>

                      {/* Step Label */}
                      <span className={`text-xs mt-2 text-center ${isActive ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                        {getStepTitle(step.id)}
                      </span>
                    </div>

                    {/* Connector Line */}
                    {idx < STEPS.length - 1 && (
                      <div className={`
                        flex-1 h-0.5 -mt-8
                        ${step.id < currentStep ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}
                      `} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </DialogHeader>

        {/* Step Content */}
        <div className="flex-1 py-6 overflow-y-auto">
          {getCurrentStepComponent()}
        </div>

        {/* Footer with Navigation (conditionally shown) */}
        {currentStep !== 4 && currentStep !== 5 && (
          <div className="flex justify-between pt-4 border-t">
            {currentStep === 1 ? (
              <div></div>
            ) : (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={!canGoBack}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('common.back')}
              </Button>
            )}

            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('bulkUpload.stepProgress', { current: currentStep, total: STEPS.length })}
            </div>

            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className={canProceed() ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {currentStep === 1 && t('bulkUpload.steps.upload.next')}
              {currentStep === 2 && t('bulkUpload.steps.mapping.next')}
              {currentStep === 3 && t('bulkUpload.steps.preview.next')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
